/**
 * @class SectionDialogLoader
 * @extends HTMLElement
 * @description A class for loading a section and opening the dialog in that section
 * @property {String} pathname - The context url of the section to fetch, can be a product, page, or collection etc.
 * @property {String} handle - unique identifier used to call the section.
 * @property {String} sectionToFetch - The name of the section to fetch
 */
class SectionDialogLoader extends HTMLElement {
  #locationPathname = this.getAttribute('location-pathname') || window.location.pathname;
  #uniqueHandle = this.getAttribute('unique-handle') || null;
  #sectionToFetch = this.getAttribute('section-to-fetch') || null;
  #listenForClose;
  #button;

  connectedCallback() {
    if (!this.#locationPathname || !this.#uniqueHandle || !this.#sectionToFetch) return;
    this.#button = this.querySelector('button[type="button"]');
    this.#button?.addEventListener('click', () => this.#loadSection());
  }

  async #loadSection() {
    this.#setLoading(true);
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('section_id', this.#sectionToFetch);

    try {
      const response = await fetch(`${this.#locationPathname}?${searchParams.toString()}`);
      if (!response.ok) throw new Error(response.statusText);
      const text = await response.text();
      const html = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('.shopify-section').innerHTML;
      document.body.insertAdjacentHTML('beforeend', html);

      publish('dialog:open', { dialogId: this.#uniqueHandle });

      this.#setupCleanup();
    } catch (error) {
      console.error(error);
    } finally {
      this.#setLoading(false);
    }
  }

  #setLoading(state = true) {
    state ? this.#button.classList.add('loading') : this.#button.classList.remove('loading');
    state ? this.#button.setAttribute('disabled', true) : this.#button.removeAttribute('disabled');
  }

  #setupCleanup() {
    this.#listenForClose = () => {
      const dialog = document.getElementById(this.#uniqueHandle);
      dialog?.remove();
      document.removeEventListener('close', this.#listenForClose);
    };

    document
      .querySelector(`dialog#${this.#uniqueHandle}`)
      ?.addEventListener('close', this.#listenForClose);
  }
}

if (!customElements.get('section-dialog-loader')) {
  customElements.define('section-dialog-loader', SectionDialogLoader);
}
